"use server";

import { cookies } from "next/headers";
import { redirect } from "next/navigation";
import { z } from "zod";
import { prisma } from "@/lib/prisma";
import { hashPassword, verifyPassword } from "@/lib/password";
import { createHandoffToken, createSessionToken, SESSION_COOKIE } from "@/lib/jwt";
import { generateUniqueSlug } from "@/lib/slug";
import { tenantUrl } from "@/lib/domain";

const signupSchema = z.object({
  organizationName: z.string().trim().min(2, "Enter your company name"),
  name: z.string().trim().min(2, "Enter your name"),
  email: z.string().trim().toLowerCase().email("Enter a valid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

export type FormState = { error?: string } | undefined;

export async function signupAction(_prev: FormState, formData: FormData): Promise<FormState> {
  const parsed = signupSchema.safeParse({
    organizationName: formData.get("organizationName"),
    name: formData.get("name"),
    email: formData.get("email"),
    password: formData.get("password"),
  });

  if (!parsed.success) {
    return { error: parsed.error.issues[0]?.message ?? "Please check your details" };
  }

  const { organizationName, name, email, password } = parsed.data;

  const existing = await prisma.user.findUnique({ where: { email } });
  if (existing) {
    return { error: "An account with that email already exists. Try logging in instead." };
  }

  const slug = await generateUniqueSlug(organizationName);
  const passwordHash = await hashPassword(password);

  const organization = await prisma.organization.create({
    data: {
      name: organizationName,
      slug,
      users: {
        create: {
          name,
          email,
          passwordHash,
          role: "OWNER",
        },
      },
    },
    include: { users: true },
  });

  const user = organization.users[0];
  const handoff = await createHandoffToken({
    sub: user.id,
    orgId: organization.id,
    orgSlug: organization.slug,
    role: user.role,
    name: user.name,
    email: user.email,
  });

  redirect(tenantUrl(slug, `/api/auth/callback?token=${handoff}&next=/dashboard`));
}

const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email("Enter a valid email address"),
  password: z.string().min(1, "Enter your password"),
});

export async function loginAction(_prev: FormState, formData: FormData): Promise<FormState> {
  const parsed = loginSchema.safeParse({
    email: formData.get("email"),
    password: formData.get("password"),
  });

  if (!parsed.success) {
    return { error: parsed.error.issues[0]?.message ?? "Please check your details" };
  }

  const { email, password } = parsed.data;

  const user = await prisma.user.findUnique({
    where: { email },
    include: { organization: true },
  });

  if (!user || !(await verifyPassword(password, user.passwordHash))) {
    return { error: "Incorrect email or password" };
  }

  const sessionPayload = {
    sub: user.id,
    orgId: user.organizationId,
    orgSlug: user.organization.slug,
    role: user.role,
    name: user.name,
    email: user.email,
    isPlatformAdmin: user.isPlatformAdmin,
  };

  // Platform admins land on /platform-admin, which lives on the root domain
  // (it manages every tenant, not just one), so the session cookie is set
  // right here rather than handed off to a tenant subdomain.
  if (user.isPlatformAdmin) {
    const sessionToken = await createSessionToken(sessionPayload);
    const cookieStore = await cookies();
    cookieStore.set(SESSION_COOKIE, sessionToken, {
      httpOnly: true,
      secure: process.env.NODE_ENV === "production",
      sameSite: "lax",
      path: "/",
      maxAge: 60 * 60 * 24 * 30,
    });
    redirect("/platform-admin");
  }

  const handoff = await createHandoffToken(sessionPayload);
  const next = String(formData.get("next") || "/dashboard");
  redirect(tenantUrl(user.organization.slug, `/api/auth/callback?token=${handoff}&next=${next}`));
}
